import { extent } from 'd3-array';

import { ScaleMetadata } from './yAxis.js';
import { MinMax } from '../../core/types/min-max.js';
import {
  TimeseriesDatapoint,
  type TimeseriesUnit,
} from '../../core/types/timeseries.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  TimeseriesBandDataPoint,
  type TimeseriesBandSlotProps,
} from '../types/timeseries-band.js';
import {
  TimeseriesVariantSlotProps,
  WeakMapDatapoints,
  WeakMapSeries,
  type ChartVariantInternal,
  type TimeseriesSlotProps,
} from '../types/timeseries.js';

type SeriesWithVariant =
  | {
      series: TimeseriesBandSlotProps[];
      variant: Extract<ChartVariantInternal, 'band'>;
    }
  | {
      series: TimeseriesVariantSlotProps[];
      variant: Exclude<ChartVariantInternal, 'band'>;
    };
type DatapointsWithVariant =
  | {
      datapoints: TimeseriesBandDataPoint[];
      variant: Extract<ChartVariantInternal, 'band'>;
    }
  | {
      datapoints: TimeseriesDatapoint[];
      variant: Exclude<ChartVariantInternal, 'band'>;
    };

const getValueOrGap = (value: number | undefined, isGap: boolean) =>
  isGap ? undefined : value ?? 0;

const getYTimeSeries = (
  datapoint: TimeseriesDatapoint,
  datapointsMap: WeakMapDatapoints,
): ReturnType<typeof getValueOrGap> => {
  const metadata = safeGet(datapointsMap, datapoint);

  return getValueOrGap(metadata.value, metadata.isGap);
};

const getYBand0 = (
  datapoint: TimeseriesBandDataPoint,
  datapointsMap: WeakMapDatapoints,
): ReturnType<typeof getValueOrGap> => {
  const metadata = safeGet(datapointsMap, datapoint);

  return getValueOrGap(metadata.y0, metadata.isGap);
};

const getYBand1 = (
  datapoint: TimeseriesBandDataPoint,
  datapointsMap: WeakMapDatapoints,
): ReturnType<typeof getValueOrGap> => {
  const metadata = safeGet(datapointsMap, datapoint);

  return getValueOrGap(metadata.y1, metadata.isGap);
};

const getDomainByUnit = (
  unit: TimeseriesUnit | undefined,
  seriesMap: WeakMapSeries,
  datapointsMap: WeakMapDatapoints,
  data: SeriesWithVariant,
) => {
  const datapoints = data.series
    .filter((singleSeries) => safeGet(seriesMap, singleSeries).unit === unit)
    .flatMap(
      (currentSeries) =>
        currentSeries.datapoints as DatapointsWithVariant['datapoints'][number][],
    );

  const { min, max } = getMinMax(datapointsMap, {
    variant: data.variant,
    datapoints,
  } as DatapointsWithVariant);

  return {
    min,
    max,
    unit,
  };
};

/**
 * Calculates the yAxis value-range depending on a given timeseries-array each containing data-points.
 * Depending on the passed chart-variant, the value-range can be:
 *  - non-stacked (e.g.: line): range from min value to max value across all datapoints for all series
 *  - stacked (e.g.: area): range from the sum of all min values to the sum of all max values across all datapoints grouped by timestamp
 *
 * @param series - The series that serves as base for calculating the value-range of the datapoints.
 * @param variant - The variant (or type) of the Chart, e.g. 'line', 'area', 'bar', 'band'.
 * @param leftAxisMetadata - metadata set from previous geometries on the left axis
 * @param rightAxisMetadata - metadata set from previous geometries on the right axis
 * @param seriesMap - series metadata map
 * @param datapointsMap - data points metadata map
 * @returns an object containing [key] as unit of the scale and value as the value-ranges for the given series depending on the chart-variant
 */
export function getYAxisValueDomainsByUnit(
  series: TimeseriesSlotProps[],
  variant: ChartVariantInternal,
  leftAxisMetadata: ScaleMetadata,
  rightAxisMetadata: ScaleMetadata,
  seriesMap: WeakMapSeries,
  datapointsMap: WeakMapDatapoints,
): ScaleMetadata[] {
  if (series.length === 0) {
    return [];
  }

  const otherGeometriesUnits = [
    leftAxisMetadata.unit,
    rightAxisMetadata.unit,
  ].filter((u) => u !== undefined);
  const seriesUnit = series.map((_series) => safeGet(seriesMap, _series).unit);
  const scaleUnits = [...new Set([...otherGeometriesUnits, ...seriesUnit])];

  return scaleUnits.map((unit) =>
    getDomainByUnit(unit, seriesMap, datapointsMap, {
      series,
      variant,
    } as SeriesWithVariant),
  );
}

export const getLineMinMax = (
  datapointsMap: WeakMapDatapoints,
  datapoints: TimeseriesDatapoint[],
): MinMax => {
  const [min = 0, max = 0] = extent(datapoints, (dp) =>
    getYTimeSeries(dp, datapointsMap),
  );

  return { min, max };
};

export const getStackedMinMax = (
  datapointsMap: WeakMapDatapoints,
  datapoints: TimeseriesDatapoint[],
): MinMax => {
  const [min = 0, max = 0] = extent(
    datapoints.map((datapoint) => {
      const metadataDatapoint = safeGet(datapointsMap, datapoint);

      if (metadataDatapoint.isGap) {
        return undefined;
      }

      return metadataDatapoint.cumulativeValue ?? 0;
    }) as number[],
  );

  return { min, max };
};

export const getBandMinMax = (
  datapointsMap: WeakMapDatapoints,
  datapoints: TimeseriesBandDataPoint[],
): MinMax => {
  const [minY0 = 0, maxY0 = 0] = extent(datapoints, (dp) =>
    getYBand0(dp, datapointsMap),
  );
  const [minY1 = 0, maxY1 = 0] = extent(datapoints, (dp) =>
    getYBand1(dp, datapointsMap),
  );
  const min = Math.min(minY0, minY1);
  const max = Math.max(maxY0, maxY1);

  return { min, max };
};

const MinMaxStrategyMap = {
  band: getBandMinMax,
  line: getLineMinMax,
  area: getStackedMinMax,
  bar: getStackedMinMax,
};

const getMinMax = (
  datapointsMap: WeakMapDatapoints,
  data: DatapointsWithVariant,
): MinMax => {
  return MinMaxStrategyMap[data.variant](
    datapointsMap,
    data.datapoints as (TimeseriesBandDataPoint & TimeseriesDatapoint)[],
  );
};
